import gsap from 'gsap';
import { PageEvent } from './page-lifecycle/PageEvent';
import { CSSClass } from './constants-theme';
import MenuItem from './MenuItem';

const DEBUG_VERBOSE = false;
const CLASS_NAME = `HHDSMenuHorizontal`;

class HHDSMenuHorizontal extends EventTarget {
	private menuEl = document.querySelector('.menu-horizontal') as HTMLElement;

	private topLevelListEls!: HTMLElement[];
	private allListEls!: HTMLElement[];
	private topLevelMenuItemsWithSubMenuEls!: HTMLElement[];
	// private subMenuListEls!: HTMLElement[];

	private contextAnimateSubMenu!: gsap.Context;

	private navigatingHasBeenTriggered = false;

	private menuItems: MenuItem[] = [];

	constructor() {
		super();
		DEBUG_VERBOSE && console.log(CLASS_NAME);
	}

	init() {
		DEBUG_VERBOSE && console.log(CLASS_NAME, this.init.name, this.menuEl);

		this.addMenuListeners();

		this.addPageEventListeners();
	}

	// ////////////////////////////////////////////////////////////////////

	private addMenuListeners() {
		const topLevelListSelector = `.${CSSClass.MenuHorizontalItemsList} > .${CSSClass.MenuItem}`;
		this.topLevelListEls = [...this.menuEl.querySelectorAll(topLevelListSelector)] as HTMLElement[];

		const topLevelListWithSubMenuSelector = `.${CSSClass.MenuHorizontalItemsList} > .${CSSClass.MenuItem}:has(.${CSSClass.SubMenu})`;
		this.topLevelMenuItemsWithSubMenuEls = [...this.menuEl.querySelectorAll(topLevelListWithSubMenuSelector)] as HTMLLIElement[];

		const allMenuItemsSelector = `.${CSSClass.MenuHorizontalItemsList} .${CSSClass.MenuItem}`;
		this.allListEls = [...this.menuEl.querySelectorAll(allMenuItemsSelector)] as HTMLLIElement[];

		this.allListEls.forEach((listEl) => {
			this.menuItems.push(new MenuItem(listEl));
		});

		// const subMenuListSelector = `.${CSSClass.MenuHorizontalItemsList} .${CSSClass.SubMenu} .${CSSClass.MenuItem}`;
		// this.subMenuListEls = [...this.menuEl.querySelectorAll(subMenuListSelector)] as HTMLLIElement[];

		// DEBUG_VERBOSE && console.log(CLASS_NAME, `this.subMenuListEls`, this.subMenuListEls);

		this.topLevelListEls.forEach((listEl) => {
			listEl.addEventListener('mouseenter', this.onMouseEnter);
			listEl.addEventListener('mouseleave', this.onMouseLeave);
			listEl.addEventListener('click', () => {
				this.setNavigatingHasBeenTriggered();
			});
		});

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		// @a11y
		// We want to handle users who tab through the page
		// Focus only jumps to items sepcifically with a tab index or known elements like a and button

		const topLevelAnchorEls = [
			...this.menuEl.querySelectorAll(`.${CSSClass.MenuHorizontalItemsList} > .${CSSClass.MenuItem} > a`),
		] as HTMLAnchorElement[];

		topLevelAnchorEls.forEach((anchorEl) => {
			anchorEl.addEventListener('focus', this.onMouseEnter);
		});

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		// We want to close all the submenus when navigating home

		const navigateHomeCallback = () => {
			DEBUG_VERBOSE && logger.log(CLASS_NAME, `navigateHomeCallback`);

			this.setNavigatingHasBeenTriggered();

			// Navigating home so force all submenus to close
			this.forceAllSubMmenusToClose();
		};

		const homeLinkEl = this.menuEl.querySelector('.menu-horizontal__logo a') as HTMLAnchorElement;

		if (homeLinkEl) homeLinkEl.addEventListener('click', navigateHomeCallback);

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		// Init the current menu

		this.openActiveMenu();
	}

	// ////////////////////////////////////////////////////////////////////

	private openActiveMenu() {
		if (!this.activeMenuItem) return;

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		const subMenuEl = this.activeMenuItem.querySelector(`.${CSSClass.SubMenu}`) as HTMLElement;

		if (subMenuEl) this.openSubMenu(subMenuEl, false);
	}

	// ////////////////////////////////////////////////////////////////////

	addPageEventListeners(): void {
		document.addEventListener(PageEvent.enter, (event: any) => {
			const parser = new DOMParser();

			let dom = parser.parseFromString(event.detail?.next?.html, 'text/html');
			this.refreshMenu(dom);
		});
	}

	// ////////////////////////////////////////////////////////////////////

	public refreshMenu(newPage: Document) {
		// Update the existing menu with classes from the next one
		// Ensures all menu classes are correct

		DEBUG_VERBOSE && logger.log(CLASS_NAME, { newPage }, newPage.toString());

		// On first run the body contains "undefined" and therefore no useful information so abort
		if (newPage.body && newPage.body.innerHTML.includes('undefined')) return;

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		const selector = `.${CSSClass.MenuHorizontalItemsList}`;
		const nextMenuEl = newPage.querySelector(selector) as HTMLElement;

		const existingMenuEl = this.menuEl.querySelector(selector) as HTMLElement;

		if (!nextMenuEl || !existingMenuEl) return;

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		// Loop over the existing menu items and copy classes from the next page's menu items.
		// Preserves the state of the menu. e.g. current-menu-item is updated to the correct element
		const nextListEls = [...nextMenuEl.querySelectorAll('li')] as HTMLElement[];
		const existingListEls = [...existingMenuEl.querySelectorAll('li')] as HTMLElement[];

		if (nextListEls.length !== existingListEls.length) throw new Error("Menu nodes count don't match!");

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		nextListEls.forEach((nextListEl, index) => {
			const existingListEl = existingListEls[index];

			existingListEl.className = nextListEl.className;

			const attribute = 'aria-current';

			const internalAnchorEl = existingListEl.querySelector('a') as HTMLElement;

			// Update the aria attribute for the current page
			if (existingListEl.classList.contains('current-menu-item')) {
				internalAnchorEl.setAttribute(attribute, 'true');
			} else {
				internalAnchorEl.removeAttribute(attribute);
			}

			this.closeSubMenusExceptActive();
		});

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		// Reset the menuitems after menu update

		this.menuItems.forEach((menuItem) => {
			menuItem.init();
		});

		this.setNavigatingHasBeenTriggered(false);
	}

	// ////////////////////////////////////////////////////////////////////

	setNavigatingHasBeenTriggered = (state = true) => {
		this.navigatingHasBeenTriggered = state;
	};

	onMouseEnter = (event: FocusEvent) => {
		DEBUG_VERBOSE && logger.log(CLASS_NAME, `onMouseEnter`);

		// Do nothing if navigation has been triggered
		if (this.navigatingHasBeenTriggered) return;

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		let targetMenuItemEl = event.target as HTMLElement;

		let skipCheck = false;

		// a11y - We've triggered a focusin from an anchor
		// Can happen when using tabbing to get to links
		if (event.target instanceof HTMLAnchorElement) {
			targetMenuItemEl = event.target.parentElement as HTMLElement;

			const activeMenuItem = this.activeMenuItem;
			const anchorEl = activeMenuItem ? activeMenuItem.querySelector('a') : null;

			skipCheck = anchorEl === document.activeElement;
		}

		DEBUG_VERBOSE && console.log(CLASS_NAME, 'onMouseEnter', `targetMenuItemEl`, targetMenuItemEl);

		if (!targetMenuItemEl) return;

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		// Don't do anything if we've rolled over the active menu item
		if (!skipCheck) {
			if (targetMenuItemEl === this.activeMenuItem) return;
		}

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		if (targetMenuItemEl.classList.contains(CSSClass.MenuItemHasChildren)) {
			this.topLevelMenuItemsWithSubMenuEls.forEach((menuItemWithSubMenuEl) => {
				const subMenuEl = menuItemWithSubMenuEl.querySelector(`.${CSSClass.SubMenu}`) as HTMLElement;

				if (menuItemWithSubMenuEl === targetMenuItemEl) {
					this.openSubMenu(subMenuEl);
				} else {
					this.closeSubMenu(subMenuEl);
				}
			});
		} else {
			// Menu item does not have a submenu to close all sub-menus
			this.topLevelMenuItemsWithSubMenuEls.forEach((menuItemWithSubMenuEl) => {
				const subMenuEl = menuItemWithSubMenuEl.querySelector(`.${CSSClass.SubMenu}`) as HTMLElement;

				this.closeSubMenu(subMenuEl);
			});
		}
	};

	// ////////////////////////////////////////////////////////////////////

	onMouseLeave = (_event: FocusEvent) => {
		DEBUG_VERBOSE && logger.log(CLASS_NAME, `onMouseLeave`);

		// Do nothing if navigation has been triggered

		if (this.navigatingHasBeenTriggered) return;

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		this.closeSubMenusExceptActive();
	};

	// ////////////////////////////////////////////////////////////////////

	closeSubMenusExceptActive() {
		this.topLevelMenuItemsWithSubMenuEls.forEach((menuItemWithSubMenu) => {
			const subMenuEl = menuItemWithSubMenu.querySelector(`.${CSSClass.SubMenu}`) as HTMLElement;

			if (!subMenuEl) throw new Error('Unable to determine sub menu');

			// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

			if (menuItemWithSubMenu === this.activeMenuItem) {
				// Re-open the active link menu
				this.openSubMenu(subMenuEl, false);
			} else {
				this.closeSubMenu(subMenuEl);
			}
		});
	}

	// ////////////////////////////////////////////////////////////////////

	forceAllSubMmenusToClose() {
		this.topLevelMenuItemsWithSubMenuEls.forEach((menuItemWithSubMenu) => {
			const subMenuEl = menuItemWithSubMenu.querySelector(`.${CSSClass.SubMenu}`) as HTMLElement;

			if (!subMenuEl) throw new Error('Unable to determine sub menu');

			this.closeSubMenu(subMenuEl);
		});
	}

	// ////////////////////////////////////////////////////////////////////

	openSubMenu(subMenuEl: HTMLElement, withAnimation = true) {
		subMenuEl.classList.add(CSSClass.ExpandSubMenu);
		subMenuEl.setAttribute('aria-expanded', 'true');

		if (this.contextAnimateSubMenu) this.contextAnimateSubMenu.revert();

		if (!withAnimation) return;

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		this.contextAnimateSubMenu = gsap.context(() => {
			const links = subMenuEl.querySelectorAll('a');
			gsap.from(links, {
				delay: 0.1,
				stagger: 0.1,
				opacity: 0,
			});
		});
	}

	// ////////////////////////////////////////////////////////////////////

	closeSubMenu(element: HTMLElement) {
		element.classList.remove(CSSClass.ExpandSubMenu);
		element.setAttribute('aria-expanded', 'false');
	}

	// ////////////////////////////////////////////////////////////////////

	get activeMenuItem() {
		const selector = `.${CSSClass.MenuHorizontalItemsList} > .${CSSClass.MenuItem}.${CSSClass.CurrentMenuParent}, .${CSSClass.MenuHorizontalItemsList} > .${CSSClass.MenuItem}.${CSSClass.CurrentMenuItem}`;
		return this.menuEl.querySelector(selector) as HTMLElement;
	}

	get activeSubMenuItem() {
		return this.menuEl.querySelector(
			`${CSSClass.MenuHorizontalItemsList} > .${CSSClass.MenuItem}.${CSSClass.CurrentMenuParent} .${CSSClass.SubMenu} .${CSSClass.CurrentMenuItem}`
		) as HTMLElement;
	}
}

export default new HHDSMenuHorizontal();
